import { useRouteError, isRouteErrorResponse } from 'react-router-dom';
import {
  ArrowPathIcon,
  HomeIcon,
  ClipboardDocumentIcon,
  MagnifyingGlassIcon,
  TrashIcon,
  CommandLineIcon,
} from '@heroicons/react/24/outline';

interface RouteErrorDetails {
  status: number | null;
  statusText: string;
  message: string;
  stack: string | null;
  data: unknown;
}

export function RouterErrorBoundary() {
  const error = useRouteError();
  const timestamp = new Date().toISOString();

  // Extract comprehensive error details
  const getErrorDetails = (): RouteErrorDetails => {
    if (isRouteErrorResponse(error)) {
      return {
        status: error.status,
        statusText: error.statusText || 'Unknown Status',
        message: error.data?.message || error.statusText || 'Route error',
        stack: null,
        data: error.data,
      };
    }

    if (error instanceof Error) {
      return {
        status: null,
        statusText: error.name,
        message: error.message,
        stack: error.stack || null,
        data: null,
      };
    }

    return {
      status: null,
      statusText: 'Unknown Error',
      message: String(error),
      stack: null,
      data: error,
    };
  };

  const errorDetails = getErrorDetails();

  // Parse stack trace into structured frames
  const parseStackTrace = (
    stack: string | null
  ): { fn: string; file: string; line: string }[] => {
    if (!stack) return [];

    const lines = stack.split('\n').slice(1); // Skip error message
    return lines
      .map((line) => {
        const match = line.match(/at\s+(.+?)\s+\((.+):(\d+):(\d+)\)/);
        if (match) {
          return {
            fn: match[1],
            file: match[2],
            line: `${match[3]}:${match[4]}`,
          };
        }
        const simpleMatch = line.match(/at\s+(.+):(\d+):(\d+)/);
        if (simpleMatch) {
          return {
            fn: '(anonymous)',
            file: simpleMatch[1],
            line: `${simpleMatch[2]}:${simpleMatch[3]}`,
          };
        }
        return null;
      })
      .filter(Boolean) as { fn: string; file: string; line: string }[];
  };

  const stackFrames = parseStackTrace(errorDetails.stack);

  // Log to console with full details
  // eslint-disable-next-line no-console
  console.group('🚨 Router Error Boundary Triggered');
  // eslint-disable-next-line no-console
  console.error('Error:', error);
  // eslint-disable-next-line no-console
  console.log('Timestamp:', timestamp);
  // eslint-disable-next-line no-console
  console.log('URL:', window.location.href);
  // eslint-disable-next-line no-console
  console.groupEnd();

  const handleReload = (): void => {
    window.location.reload();
  };

  const handleGoHome = (): void => {
    window.location.href = '/';
  };

  const handleClearStorageAndReload = (): void => {
    localStorage.clear();
    sessionStorage.clear();
    window.location.reload();
  };

  const handleSearchError = (): void => {
    const searchQuery = encodeURIComponent(
      `${errorDetails.statusText}: ${errorDetails.message}`
    );
    window.open(`https://www.google.com/search?q=${searchQuery}`, '_blank');
  };

  const handleCopyReport = async (): Promise<void> => {
    const report = `
═══════════════════════════════════════════════════════════════
                     ROUTER ERROR REPORT
═══════════════════════════════════════════════════════════════

TIMESTAMP: ${timestamp}
URL: ${window.location.href}
USER AGENT: ${navigator.userAgent}

─────────────────────────────────────────────────────────────────
ERROR DETAILS
─────────────────────────────────────────────────────────────────
${errorDetails.status ? `STATUS: ${errorDetails.status}` : ''}
TYPE: ${errorDetails.statusText}
MESSAGE: ${errorDetails.message}

${
  errorDetails.data
    ? `─────────────────────────────────────────────────────────────────
RESPONSE DATA
─────────────────────────────────────────────────────────────────
${JSON.stringify(errorDetails.data, null, 2)}`
    : ''
}

${
  errorDetails.stack
    ? `─────────────────────────────────────────────────────────────────
STACK TRACE
─────────────────────────────────────────────────────────────────
${errorDetails.stack}`
    : ''
}

─────────────────────────────────────────────────────────────────
ENVIRONMENT
─────────────────────────────────────────────────────────────────
NODE_ENV: ${process.env.NODE_ENV}
VIEWPORT: ${window.innerWidth}x${window.innerHeight}
LANGUAGE: ${navigator.language}
PLATFORM: ${navigator.platform}
ONLINE: ${navigator.onLine}

═══════════════════════════════════════════════════════════════
`.trim();

    await navigator.clipboard.writeText(report);
    // eslint-disable-next-line no-alert
    alert('Error report copied to clipboard!');
  };

  return (
    <main className='min-h-screen bg-gray-900 text-gray-100'>
      {/* Error Header */}
      <header className='bg-red-600 px-6 py-4'>
        <div className='max-w-7xl mx-auto flex items-center justify-between'>
          <div className='flex items-center gap-3'>
            <CommandLineIcon className='h-8 w-8 text-white' />
            <div>
              <h1 className='text-xl font-bold text-white'>
                Router Error {errorDetails.status && `(${errorDetails.status})`}
              </h1>
              <p className='text-red-100 text-sm font-mono'>
                {errorDetails.statusText}: {errorDetails.message}
              </p>
            </div>
          </div>
          <span className='text-red-200 text-xs font-mono'>{timestamp}</span>
        </div>
      </header>

      <div className='max-w-7xl mx-auto px-6 py-8'>
        {/* Quick Actions */}
        <section className='mb-8'>
          <h2 className='text-sm font-semibold text-gray-400 uppercase tracking-wider mb-3'>
            Quick Actions
          </h2>
          <div className='flex flex-wrap gap-3'>
            <button
              type='button'
              onClick={handleReload}
              className='inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium'
            >
              <ArrowPathIcon className='h-4 w-4' />
              Reload Page
            </button>
            <button
              type='button'
              onClick={handleGoHome}
              className='inline-flex items-center gap-2 px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600 transition-colors text-sm font-medium'
            >
              <HomeIcon className='h-4 w-4' />
              Go Home
            </button>
            <button
              type='button'
              onClick={handleClearStorageAndReload}
              className='inline-flex items-center gap-2 px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors text-sm font-medium'
            >
              <TrashIcon className='h-4 w-4' />
              Clear Storage & Reload
            </button>
            <button
              type='button'
              onClick={handleSearchError}
              className='inline-flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors text-sm font-medium'
            >
              <MagnifyingGlassIcon className='h-4 w-4' />
              Search Error
            </button>
            <button
              type='button'
              onClick={handleCopyReport}
              className='inline-flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm font-medium'
            >
              <ClipboardDocumentIcon className='h-4 w-4' />
              Copy Full Report
            </button>
          </div>
        </section>

        <div className='grid lg:grid-cols-3 gap-8'>
          {/* Main Content */}
          <div className='lg:col-span-2 space-y-6'>
            {/* Error Details */}
            <section className='bg-gray-800 rounded-xl p-6'>
              <h2 className='text-sm font-semibold text-gray-400 uppercase tracking-wider mb-4'>
                Error Details
              </h2>
              <div className='space-y-4'>
                {errorDetails.status && (
                  <div>
                    <span className='text-gray-500 text-xs uppercase tracking-wider'>
                      HTTP Status
                    </span>
                    <p className='text-4xl font-bold text-red-400'>
                      {errorDetails.status}
                    </p>
                  </div>
                )}
                <div>
                  <span className='text-gray-500 text-xs uppercase tracking-wider'>
                    Error Type
                  </span>
                  <p className='text-lg font-mono text-yellow-400'>
                    {errorDetails.statusText}
                  </p>
                </div>
                <div>
                  <span className='text-gray-500 text-xs uppercase tracking-wider'>
                    Message
                  </span>
                  <p className='text-gray-200 font-mono bg-gray-900 rounded-lg p-3 break-all'>
                    {errorDetails.message}
                  </p>
                </div>
              </div>
            </section>

            {/* Response Data (for route errors) */}
            {errorDetails.data !== null && errorDetails.data !== undefined && (
              <section className='bg-gray-800 rounded-xl p-6'>
                <h2 className='text-sm font-semibold text-gray-400 uppercase tracking-wider mb-4'>
                  Response Data
                </h2>
                <pre className='bg-gray-900 rounded-lg p-4 overflow-x-auto text-sm font-mono text-gray-300'>
                  {JSON.stringify(errorDetails.data, null, 2)}
                </pre>
              </section>
            )}

            {/* Stack Trace (for JS errors) */}
            {stackFrames.length > 0 && (
              <section className='bg-gray-800 rounded-xl p-6'>
                <h2 className='text-sm font-semibold text-gray-400 uppercase tracking-wider mb-4'>
                  Stack Trace
                </h2>
                <div className='bg-gray-900 rounded-lg overflow-hidden'>
                  {stackFrames.map((frame, idx) => (
                    <div
                      key={`${frame.fn}-${frame.file}-${frame.line}`}
                      className={`px-4 py-2 font-mono text-sm border-l-2 ${
                        idx === 0
                          ? 'border-red-500 bg-red-900/20'
                          : 'border-gray-700 hover:bg-gray-800'
                      }`}
                    >
                      <span className='text-gray-500 mr-3 select-none'>
                        {idx + 1}
                      </span>
                      <span className='text-blue-400'>{frame.fn}</span>
                      <span className='text-gray-500 mx-2'>@</span>
                      <span className='text-green-400'>{frame.file}</span>
                      <span className='text-gray-500 mx-1'>:</span>
                      <span className='text-yellow-400'>{frame.line}</span>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {/* Raw Stack (fallback) */}
            {errorDetails.stack && stackFrames.length === 0 && (
              <section className='bg-gray-800 rounded-xl p-6'>
                <h2 className='text-sm font-semibold text-gray-400 uppercase tracking-wider mb-4'>
                  Raw Stack Trace
                </h2>
                <pre className='bg-gray-900 rounded-lg p-4 overflow-x-auto text-sm font-mono text-gray-300 whitespace-pre-wrap'>
                  {errorDetails.stack}
                </pre>
              </section>
            )}
          </div>

          {/* Sidebar */}
          <div className='space-y-6'>
            {/* Environment */}
            <section className='bg-gray-800 rounded-xl p-6'>
              <h2 className='text-sm font-semibold text-gray-400 uppercase tracking-wider mb-4'>
                Environment
              </h2>
              <dl className='space-y-3 text-sm'>
                <div>
                  <dt className='text-gray-500'>NODE_ENV</dt>
                  <dd className='font-mono text-cyan-400'>
                    {process.env.NODE_ENV}
                  </dd>
                </div>
                <div>
                  <dt className='text-gray-500'>Viewport</dt>
                  <dd className='font-mono text-gray-300'>
                    {window.innerWidth}x{window.innerHeight}
                  </dd>
                </div>
                <div>
                  <dt className='text-gray-500'>Language</dt>
                  <dd className='font-mono text-gray-300'>
                    {navigator.language}
                  </dd>
                </div>
                <div>
                  <dt className='text-gray-500'>Platform</dt>
                  <dd className='font-mono text-gray-300'>
                    {navigator.platform}
                  </dd>
                </div>
                <div>
                  <dt className='text-gray-500'>Online</dt>
                  <dd
                    className={`font-mono ${
                      navigator.onLine ? 'text-green-400' : 'text-red-400'
                    }`}
                  >
                    {navigator.onLine ? 'Yes' : 'No'}
                  </dd>
                </div>
              </dl>
            </section>

            {/* Request URL */}
            <section className='bg-gray-800 rounded-xl p-6'>
              <h2 className='text-sm font-semibold text-gray-400 uppercase tracking-wider mb-4'>
                Request URL
              </h2>
              <p className='font-mono text-sm text-gray-300 break-all bg-gray-900 rounded-lg p-3'>
                {window.location.href}
              </p>
            </section>

            {/* Debug Tips */}
            <section className='bg-gray-800 rounded-xl p-6'>
              <h2 className='text-sm font-semibold text-gray-400 uppercase tracking-wider mb-4'>
                Debug Tips
              </h2>
              <ul className='space-y-2 text-sm text-gray-400'>
                {errorDetails.status === 404 && (
                  <>
                    <li>• Check if the route is defined in your router</li>
                    <li>• Verify any dynamic route parameters</li>
                    <li>• Check for typos in the URL path</li>
                  </>
                )}
                {errorDetails.status === 401 && (
                  <>
                    <li>• User may need to log in again</li>
                    <li>• Check if auth token has expired</li>
                    <li>• Try clearing storage and reloading</li>
                  </>
                )}
                {errorDetails.status === 403 && (
                  <>
                    <li>• User may lack required permissions</li>
                    <li>• Check role-based access controls</li>
                    <li>• Verify feature flags/entitlements</li>
                  </>
                )}
                {errorDetails.status === 500 && (
                  <>
                    <li>• Check backend server logs</li>
                    <li>• Verify API endpoint is running</li>
                    <li>• Look for database connectivity issues</li>
                  </>
                )}
                {!errorDetails.status && (
                  <>
                    <li>• Check browser DevTools console</li>
                    <li>• Look for network request failures</li>
                    <li>• Verify component imports are correct</li>
                  </>
                )}
                <li>• Full error logged to console</li>
              </ul>
            </section>
          </div>
        </div>
      </div>
    </main>
  );
}
